import type { DrawingClientEvent } from "../../../shared/types";

/** How long consecutive stroke points may wait to share one message. Pointer
 * moves arrive once per display frame (60-144+ per second), and every one of
 * them used to be its own Socket.IO message - and its own network packet - to
 * the server and then again to every viewer. Grouping a couple of frames' worth
 * cuts that packet count several-fold for an imperceptible delay on the
 * viewers' side (the artist's own canvas still updates instantly, it never
 * waits on this). */
const FLUSH_DELAY_MS = 30;
/** Matches the server's per-batch cap (see drawingHandlers.ts), with headroom. */
const MAX_BATCHED_POINTS = 64;

/** Sits between the CanvasEditor and the socket: strokePoints for the stroke in
 * progress are buffered into one "strokePoints" message, and every other
 * command flushes that buffer first, so the server always receives commands in
 * exactly the order they were drawn. */
export class StrokeBatcher {
  private pendingId: string | null = null;
  private pendingPoints: number[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private send: (event: DrawingClientEvent) => void) {}

  push(event: DrawingClientEvent): void {
    if (event.type !== "strokePoint") {
      this.flush();
      this.send(event);
      return;
    }
    if (this.pendingId !== null && this.pendingId !== event.id) this.flush();
    this.pendingId = event.id;
    this.pendingPoints.push(event.point.x, event.point.y);
    if (this.pendingPoints.length >= MAX_BATCHED_POINTS * 2) {
      this.flush();
    } else if (this.timer === null) {
      this.timer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
    }
  }

  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const id = this.pendingId;
    const points = this.pendingPoints;
    this.pendingId = null;
    this.pendingPoints = [];
    if (id === null || points.length === 0) return;
    if (points.length === 2) {
      this.send({ type: "strokePoint", id, point: { x: points[0]!, y: points[1]! } });
    } else {
      this.send({ type: "strokePoints", id, points });
    }
  }
}
